import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { 
  getInvoiceEditDataApi, 
  createCustomerApi, 
  updateCustomerApi,
  updateCustomerAddressApi,
  deleteCustomerApi,
  validateCustomerSsnApi,
  Customer, 
  InvoiceEditResponse, 
  CreateCustomerRequest,
  UpdateCustomerRequest,
  UpdateCustomerAddressRequest
} from './customer.service';
import { fetchCustomerById as fetchCustomerByIdApi } from '../../api/customer';
import { logOut } from '../../../redux/auth/authSlice';
import { t } from 'i18next';

export const fetchInvoiceEditData = createAsyncThunk(
  'customer/fetchInvoiceEditData',
  async (userId: number) => {
    const data = await getInvoiceEditDataApi(userId);
    return data;
  }
);

export const fetchCustomerById = createAsyncThunk(
  'customer/fetchCustomerById',
  async (customerId: number, { rejectWithValue }) => {
    try {
      const data = await fetchCustomerByIdApi(customerId);
      return data;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to fetch customer';
      return rejectWithValue(message);
    }
  }
);

export const createCustomer = createAsyncThunk(
  'customer/createCustomer',
  async (customerData: CreateCustomerRequest, { rejectWithValue }) => {
    try {
      const response = await createCustomerApi(customerData);
      // Backend may return HTTP 200 but signal failure via the body (statusCode/message).
      const bodyStatus = response?.statusCode;
      const bodyMessage = response?.message || '';
      if ((typeof bodyStatus === 'number' && bodyStatus >= 400) || bodyMessage.includes('Ogiltigt VAT-nummer')) {
        const errorMsg = bodyMessage.includes('Ogiltigt VAT-nummer')
          ? t('INVALID_VAT_NUMBER_ERROR')
          : (bodyMessage || 'Failed to create customer');
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
      }
      return response.response; // Return the customer data from the response
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message || error?.message || '';
      if (responseMessage.includes('Ogiltigt VAT-nummer')) {
        const errorMsg = t('INVALID_VAT_NUMBER_ERROR');
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
      }
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to create customer';
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

export const updateCustomer = createAsyncThunk(
  'customer/updateCustomer',
  async (customerData: UpdateCustomerRequest, { rejectWithValue }) => {
    try {
      const response = await updateCustomerApi(customerData);
      // Backend may return HTTP 200 but signal failure via the body (statusCode/message).
      const bodyStatus = response?.statusCode;
      const bodyMessage = response?.message || '';
      if ((typeof bodyStatus === 'number' && bodyStatus >= 400) || bodyMessage.includes('Ogiltigt VAT-nummer')) {
        const errorMsg = bodyMessage.includes('Ogiltigt VAT-nummer')
          ? t('INVALID_VAT_NUMBER_ERROR')
          : (bodyMessage || 'Failed to update customer');
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
      }
      return response.response; // Return the updated customer data from the response
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message || error?.message || '';
      if (responseMessage.includes('Ogiltigt VAT-nummer')) {
        const errorMsg = t('INVALID_VAT_NUMBER_ERROR');
        toast.error(errorMsg);
        return rejectWithValue(errorMsg);
      }
      const errorMsg = error?.response?.data?.message || error?.message || 'Failed to update customer';
      toast.error(errorMsg);
      return rejectWithValue(errorMsg);
    }
  }
);

export const updateCustomerAddress = createAsyncThunk(
  'customer/updateCustomerAddress',
  async (addressData: UpdateCustomerAddressRequest, { rejectWithValue }) => {
    try {
      const response = await updateCustomerAddressApi(addressData);
      toast.success(t('Billing address updated successfully', { returnObjects: false }) as string);
      return response.response;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to update address';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const validateCustomerSsn = createAsyncThunk(
  'customer/validateCustomerSsn',
  async ({ customerId, lang }: { customerId: number; lang: string }, { rejectWithValue }) => {
    try {
      const response = await validateCustomerSsnApi(customerId, lang);
      // Check statusCode in response body — only 200 or 201 are valid
      const statusCode = response?.statusCode ?? response?.status;
      if (statusCode !== 200 && statusCode !== 201) {
        const message = response?.message || 'Invalid social security number';
        return rejectWithValue(message);
      }
      return response;
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Invalid social security number';
      return rejectWithValue(message);
    }
  }
);

export const deleteCustomer = createAsyncThunk<number, number, { rejectValue: string }>(

  'customer/deleteCustomer',
  async (customerId, { rejectWithValue }) => {
    try {
      await deleteCustomerApi(customerId);
      return customerId;
    } catch (error: any) {
      const message = error?.response?.data?.message || error?.message || 'Failed to delete customer';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

type Awaited<T> = T extends Promise<infer U> ? U : T;

interface SelectedCustomer {
  id: string;
  customerId?: string | number;
  name: string;
  billingAddress: string;
  email?: string;
  secondaryEmailAddress?: string;
  accountType?: string;
  country?: string;
  paymentTerm?: string | number;
  hasAnyInvoice?: boolean;
}

type CustomerState = {
  customers: Customer[];
  user: any;
  freelancers: any[];
  selectedFreelancersForInvoice: any[]; // Freelancers selected in collective invoice
  showFreelancerNamesOnPdf: boolean; // When false, freelancer names are hidden from the customer on the invoice PDF
  invoiceEditData: Awaited<ReturnType<typeof getInvoiceEditDataApi>> | null;
  selectedCustomer: SelectedCustomer | null;
  selectedCustomerDetails: Customer | null;
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  createError: string | null;
  updateLoading: boolean;
  updateError: string | null;
  updateAddressLoading: boolean;
  updateAddressError: string | null;
  fetchCustomerByIdLoading: boolean;
  fetchCustomerByIdError: string | null;
  deleteLoading: boolean;
  deleteError: string | null;
  ssnValidateLoading: boolean;
  ssnValidateError: string | null;
  ssnValidationFailed: boolean;
};

const initialState: CustomerState = {
  customers: [],
  user: null,
  freelancers: [],
  selectedFreelancersForInvoice: [],
  showFreelancerNamesOnPdf: false, // default: unchecked (names hidden → hideFreelancerName=true)
  invoiceEditData: null,
  selectedCustomer: null,
  selectedCustomerDetails: null,
  loading: false,
  error: null,
  createLoading: false,
  createError: null,
  updateLoading: false,
  updateError: null,
  updateAddressLoading: false,
  updateAddressError: null,
  fetchCustomerByIdLoading: false,
  fetchCustomerByIdError: null,
  deleteLoading: false,
  deleteError: null,
  ssnValidateLoading: false,
  ssnValidateError: null,
  ssnValidationFailed: false,
};

const customerSlice = createSlice({
  name: 'customer',
  initialState,
  reducers: {
    clearCustomerData: (state) => {
      state.customers = [];
      state.invoiceEditData = null;
      state.error = null;
    },
    setSelectedCustomer: (state, action) => {
      state.selectedCustomer = action.payload;
    },
    setSelectedFreelancersForInvoice: (state, action) => {
      state.selectedFreelancersForInvoice = action.payload;
    },
    setShowFreelancerNamesOnPdf: (state, action) => {
      state.showFreelancerNamesOnPdf = action.payload;
    },
    clearCreateError: (state) => {
      state.createError = null;
    },
    clearUpdateError: (state) => {
      state.updateError = null;
    },
    clearUpdateAddressError: (state) => {
      state.updateAddressError = null;
    },
    clearDeleteError: (state) => {
      state.deleteError = null;
    },
    setSsnValidationFailed: (state, action) => {
      state.ssnValidationFailed = action.payload;
    },
    resetCustomerSelection: (state) => {
      state.selectedCustomer = null;
      state.selectedFreelancersForInvoice = [];
      state.selectedCustomerDetails = null;
      state.showFreelancerNamesOnPdf = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchInvoiceEditData.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoiceEditData.fulfilled, (state, action) => {
        state.loading = false;
        state.invoiceEditData = action.payload;
        state.customers = action.payload.response.customers;
        state.user = action.payload.response.user;
        state.freelancers = action.payload.response.freelancers;
      })
      .addCase(fetchInvoiceEditData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch invoice edit data';
      })
      .addCase(createCustomer.pending, state => {
        state.createLoading = true;
        state.createError = null;
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.createLoading = false;
        if (action.payload) {
          state.customers.push(action.payload);
        }
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.error.message || 'Failed to create customer';
      })
      .addCase(updateCustomer.pending, state => {
        state.updateLoading = true;
        state.updateError = null;
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.updateLoading = false;
        const index = state.customers.findIndex(customer => 
          (customer.customerId || customer.id) === (action.payload.customerId || action.payload.id)
        );
        if (index !== -1) {
          state.customers[index] = action.payload;
        }
        // Update selected customer if it's the same one
        if (state.selectedCustomer && 
            (state.selectedCustomer.id === action.payload.customerId?.toString() || 
             state.selectedCustomer.id === action.payload.id?.toString())) {
          state.selectedCustomer = {
            id: (action.payload.customerId || action.payload.id)?.toString() || '',
            name: action.payload.name,
            billingAddress: action.payload.address || 'No address provided'
          };
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.error.message || 'Failed to update customer';
      })
      .addCase(deleteCustomer.pending, state => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.deleteLoading = false;
        const deletedId = action.payload;
        state.customers = state.customers.filter(customer => 
          (customer.customerId || customer.id) !== deletedId
        );
        // Clear selected customer if it was deleted
        if (state.selectedCustomer && state.selectedCustomer.id === deletedId.toString()) {
          state.selectedCustomer = null;
        }
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.deleteLoading = false;
        state.deleteError = action.payload as string;
      })
      .addCase(updateCustomerAddress.pending, state => {
        state.updateAddressLoading = true;
        state.updateAddressError = null;
      })
      .addCase(updateCustomerAddress.fulfilled, (state, action) => {
        state.updateAddressLoading = false;
        const index = state.customers.findIndex(customer => 
          (customer.customerId || customer.id) === (action.payload.customerId || action.payload.id)
        );
        if (index !== -1) {
          // Update customer in the list
          state.customers[index] = {
            ...state.customers[index],
            ...action.payload
          };
        }
        // Update selected customer if it's the same one
        if (state.selectedCustomer && 
            (state.selectedCustomer.id === action.payload.customerId?.toString() || 
             state.selectedCustomer.id === action.payload.id?.toString())) {
          state.selectedCustomer = {
            id: (action.payload.customerId || action.payload.id)?.toString() || '',
            name: action.payload.name,
            billingAddress: action.payload.address || 'No address provided'
          };
        }
      })
      .addCase(updateCustomerAddress.rejected, (state, action) => {
        state.updateAddressLoading = false;
        state.updateAddressError = action.payload as string;
      })
      .addCase(fetchCustomerById.pending, state => {
        state.fetchCustomerByIdLoading = true;
        state.fetchCustomerByIdError = null;
        state.selectedCustomerDetails = null;
      })
      .addCase(validateCustomerSsn.pending, state => {
        state.ssnValidateLoading = true;
        state.ssnValidateError = null;
      })
      .addCase(validateCustomerSsn.fulfilled, state => {
        state.ssnValidateLoading = false;
        state.ssnValidateError = null;
      })
      .addCase(validateCustomerSsn.rejected, (state, action) => {
        state.ssnValidateLoading = false;
        state.ssnValidateError = action.payload as string;
      })
      .addCase(fetchCustomerById.fulfilled, (state, action) => {
        state.fetchCustomerByIdLoading = false;
        state.selectedCustomerDetails = {
          ...action.payload,
          accountType: action.payload.accountType !== undefined ? String(action.payload.accountType) : undefined,
          invoiceType: action.payload.invoiceType ?? undefined,
          secondaryEmailAddress: action.payload.secondaryEmailAddress ?? undefined
        };
      })
      .addCase(fetchCustomerById.rejected, (state, action) => {
        state.fetchCustomerByIdLoading = false;
        state.fetchCustomerByIdError = action.payload as string;
      })
      // Wipe ALL customer/invoice-selection state on logout so a different user
      // logging in (SPA nav, no page reload) never sees the previous user's
      // freelancers / selected customer / invoice edit data. Mirrors the
      // logOut reset every other auth-sensitive slice already has.
      .addCase(logOut, () => initialState);
  },
});

export const { clearCustomerData, setSelectedCustomer, setSelectedFreelancersForInvoice, setShowFreelancerNamesOnPdf, clearCreateError, clearUpdateError, clearUpdateAddressError, clearDeleteError, setSsnValidationFailed, resetCustomerSelection } = customerSlice.actions;
export default customerSlice.reducer;